import React, { useEffect, useMemo, useState } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { SweetAlertShow, LoadingDiv, formatDateTime } from "../master/Master";
import usePrivateLink from "../config/usePrivateLink";

import {
  distributionKitList,
  kitAssignmentFeeList,
  kitAssignmentFeePayments,
} from "../../api/DistributionKit";

import DistributionKitLayout from "./DistributionKitLayout";
import { useNavigate } from "react-router-dom";

export default function DistributionReport() {
  usePrivateLink();
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false);

  const [transactions, setTransactions] = useState([]);
  const [kits, setKits] = useState([]);

  const [summary, setSummary] = useState({
    records: 0,
    quantity: 0,
    kit_amount: 0,
    discount_amount: 0,
    final_amount: 0,
    paid_amount: 0,
    due_amount: 0,
  });

  const [pagination, setPagination] = useState(null);
  const [printing, setPrinting] = useState(false);

  const [search, setSearch] = useState("");

  const [filters, setFilters] = useState({
    kit_id: "",
    item_id: "",
    batch_id: "",
    recipient_type: "",
    payment_status: "",
    status: "",
  });

  const [tempFilters, setTempFilters] = useState(filters);

  const [showFilter, setShowFilter] = useState(false);

  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [paymentHistoryFee, setPaymentHistoryFee] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const navigate = useNavigate();

  const openPaymentHistory = async (fee) => {
    try {
      setPaymentHistoryFee(fee);
      setPaymentHistory([]);
      setShowPaymentHistory(true);
      setPaymentHistoryLoading(true);

      const response = await kitAssignmentFeePayments(fee.id);

      if (response?.data?.success) {
        const data = response.data.data;

        setPaymentHistoryFee(data?.fee || fee);
        setPaymentHistory(data?.transactions || []);
      } else {
        setShowPaymentHistory(false);

        SweetAlertShow(
          "error",
          response?.data?.message || "Unable to fetch payment history.",
        );
      }
    } catch (error) {
      console.error("Payment history error:", error);

      setShowPaymentHistory(false);

      SweetAlertShow(
        "error",
        error?.response?.data?.message || "Unable to fetch payment history.",
      );
    } finally {
      setPaymentHistoryLoading(false);
    }
  };

  /*
   * ----------------------------------------------------
   * Fetch Kits
   * ----------------------------------------------------
   */
  const getKits = async () => {
    try {
      const response = await distributionKitList();

      if (response?.data?.success) {
        setKits(response?.data?.data?.data || response?.data?.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const buildParams = ({ page = 1, all = false } = {}) => {
    const params = new URLSearchParams();

    if (filters.kit_id) {
      params.append("kit_id", filters.kit_id);
    }

    if (filters.item_id) {
      params.append("item_id", filters.item_id);
    }

    if (filters.batch_id) {
      params.append("batch_id", filters.batch_id);
    }

    if (filters.recipient_type) {
      params.append("recipient_type", filters.recipient_type);
    }

    if (filters.payment_status !== "") {
      params.append("payment_status", filters.payment_status);
    }

    if (filters.status !== "") {
      params.append("status", filters.status);
    }

    if (search.trim()) {
      params.append("search", search.trim());
    }

    if (all) {
      params.append("all", "1");
    } else {
      params.append("page", page);
      params.append("per_page", 20);
    }

    return params;
  };

  /*
   * ----------------------------------------------------
   * Fetch Transactions
   * ----------------------------------------------------
   */
  const getTransactions = async (page = 1) => {
    setLoading(true);

    try {
      const params = buildParams({
        page,
        all: false,
      });

      const response = await kitAssignmentFeeList(params.toString());

      if (response?.data?.success) {
        const data = response?.data?.data;

        setTransactions(data?.data || []);

        setPagination(data || null);

        setSummary(
          response?.data?.summary || {
            records: 0,
            quantity: 0,
            kit_amount: 0,
            discount_amount: 0,
            final_amount: 0,
            paid_amount: 0,
            due_amount: 0,
          },
        );
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load report.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load report.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getKits();
  }, []);

  useEffect(() => {
    getTransactions(1);
  }, [filters, search]);

  /*
   * ----------------------------------------------------
   * Stats
   * ----------------------------------------------------
   */
  const stats = useMemo(() => {
    return {
      records: Number(summary.records || 0),
      quantity: Number(summary.quantity || 0),
      finalAmount: Number(summary.final_amount || 0),
      dueAmount: Number(summary.due_amount || 0),
    };
  }, [summary]);

  /*
   * ----------------------------------------------------
   * Filter Handlers
   * ----------------------------------------------------
   */
  const openFilters = () => {
    setTempFilters(filters);
    setShowFilter(true);
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setShowFilter(false);
  };

  const clearFilters = () => {
    const emptyFilters = {
      kit_id: "",
      item_id: "",
      batch_id: "",
      recipient_type: "",
      payment_status: "",
      status: "",
    };

    setTempFilters(emptyFilters);
    setFilters(emptyFilters);
    setSearch("");
    setShowFilter(false);
  };

  const hasFilters =
    search || Object.values(filters).some((value) => value !== "");

  /*
   * ----------------------------------------------------
   * Helpers
   * ----------------------------------------------------
   */
  const getRecipient = (row) => {
    if (row.student) {
      return {
        name: row.student.name || row.student.student_name || "Student",
        type: "Student",
      };
    }

    if (row.staff) {
      return {
        name: row.staff.name || row.staff.staff_name || "Staff",
        type: "Staff",
      };
    }

    return {
      name: "—",
      type: "—",
    };
  };

  const getPaymentBadge = (status) => {
    switch (Number(status)) {
      case 2:
        return (
          <span className="badge bg-label-success rounded-pill">
            <i className="ri-checkbox-circle-line me-1" />
            Paid
          </span>
        );

      case 1:
        return (
          <span className="badge bg-label-warning rounded-pill">
            <i className="ri-time-line me-1" />
            Partial
          </span>
        );

      default:
        return (
          <span className="badge bg-label-danger rounded-pill">
            <i className="ri-error-warning-line me-1" />
            Pending
          </span>
        );
    }
  };

  const getStatusBadge = (status) => {
    if (Number(status) === 1) {
      return (
        <span className="badge bg-label-success rounded-pill">Active</span>
      );
    }

    if (Number(status) === 3) {
      return (
        <span className="badge bg-label-warning rounded-pill">Removed</span>
      );
    }

    if (Number(status) === 2) {
      return (
        <span className="badge bg-label-danger rounded-pill">Deleted</span>
      );
    }

    return (
      <span className="badge bg-label-secondary rounded-pill">Inactive</span>
    );
  };

  const handlePageChange = (page) => {
    if (
      !pagination ||
      page < 1 ||
      page > pagination.last_page ||
      page === pagination.current_page
    ) {
      return;
    }

    getTransactions(page);
  };

  const printReport = async () => {
    if (printing) return;

    setPrinting(true);

    try {
      const params = buildParams({
        all: true,
      });

      const response = await kitAssignmentFeeList(params.toString());

      if (!response?.data?.success) {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to prepare print report.",
        );
        return;
      }

      const allTransactions = response?.data?.data || [];

      const printSummary = response?.data?.summary || summary;

      if (!allTransactions.length) {
        SweetAlertShow("info", "No Data", "There are no records to print.");
        return;
      }

      const getRecipientPrint = (row) => {
        if (row.student) {
          return {
            name: row.student.name || row.student.student_name || "Student",
            type: "Student",
          };
        }

        if (row.staff) {
          return {
            name: row.staff.name || row.staff.staff_name || "Staff",
            type: "Staff",
          };
        }

        return {
          name: "—",
          type: "—",
        };
      };

      const getPaymentPrint = (status) => {
        switch (Number(status)) {
          case 2:
            return "Paid";

          case 1:
            return "Partial";

          default:
            return "Pending";
        }
      };

      const getStatusPrint = (status) => {
        switch (Number(status)) {
          case 1:
            return "Active";

          case 3:
            return "Removed";

          case 2:
            return "Deleted";

          default:
            return "Inactive";
        }
      };

      const formatAmount = (value) => {
        return `₹${Number(value || 0).toFixed(2)}`;
      };

      const rowsHtml = allTransactions
        .map((row) => {
          const recipient = getRecipientPrint(row);

          return `
          <tr>
            <td>
              <strong>${recipient.name}</strong>
              <br>
              <small>${recipient.type}</small>
            </td>

            <td>
              <strong>${row.kit?.name || "—"}</strong>
              <br>
              <small>
                ${row.item?.name ? row.item.name : "Complete Kit"}
              </small>
            </td>

            <td>
              ${row.batch?.title || "—"}
            </td>

            <td class="text-center">
              ${row.quantity || 0}
            </td>

            <td class="text-right">
              ${formatAmount(row.kit_amount)}
            </td>

            <td class="text-right">
              ${Number(row.discount_amount || 0) > 0
              ? `- ${formatAmount(row.discount_amount)}`
              : "—"
            }
            </td>

            <td class="text-right">
              <strong>
                ${formatAmount(row.final_amount)}
              </strong>
            </td>

            <td class="text-right">
              ${formatAmount(row.paid_amount)}
            </td>

            <td class="text-right">
              ${formatAmount(row.due_amount)}
            </td>

            <td>
              ${getPaymentPrint(row.payment_status)}
            </td>

            <td>
              ${getStatusPrint(row.status)}
            </td>

            <td>
              ${row.created_at ? formatDateTime(row.created_at) : "—"}
            </td>
          </tr>
        `;
        })
        .join("");

      const filterText = [];

      if (filters.kit_id) {
        const kit = kits.find(
          (item) => String(item.id) === String(filters.kit_id),
        );

        if (kit) {
          filterText.push(`Kit: ${kit.name}`);
        }
      }

      if (filters.recipient_type) {
        filterText.push(
          `Recipient: ${filters.recipient_type === "student" ? "Student" : "Staff"
          }`,
        );
      }

      if (filters.payment_status !== "") {
        const paymentMap = {
          0: "Pending",
          1: "Partial",
          2: "Paid",
        };

        filterText.push(
          `Payment: ${paymentMap[filters.payment_status] || filters.payment_status
          }`,
        );
      }

      if (filters.status !== "") {
        const statusMap = {
          1: "Active",
          2: "Deleted",
          3: "Removed",
        };

        filterText.push(
          `Status: ${statusMap[filters.status] || filters.status}`,
        );
      }

      if (search.trim()) {
        filterText.push(`Search: ${search.trim()}`);
      }

      const printWindow = window.open("", "_blank", "width=1400,height=900");

      if (!printWindow) {
        SweetAlertShow(
          "warning",
          "Popup Blocked",
          "Please allow popups for this website to print the report.",
        );
        return;
      }

      printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Distribution Report</title>

          <meta charset="UTF-8" />

          <style>
            @page {
              size: A4 landscape;
              margin: 10mm;
            }

            * {
              box-sizing: border-box;
            }

            body {
              font-family:
                Arial,
                Helvetica,
                sans-serif;
              color: #222;
              margin: 0;
              padding: 0;
              font-size: 11px;
            }

            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 15px;
              border-bottom: 2px solid #222;
              padding-bottom: 10px;
            }

            .title {
              font-size: 20px;
              font-weight: 700;
              margin-bottom: 4px;
            }

            .subtitle {
              color: #666;
              font-size: 11px;
            }

            .date {
              text-align: right;
              color: #666;
              font-size: 10px;
            }

            .filters {
              margin-bottom: 12px;
              padding: 8px 10px;
              border: 1px solid #ddd;
              background: #f8f8f8;
            }

            .summary {
              display: grid;
              grid-template-columns:
                repeat(5, 1fr);
              gap: 8px;
              margin-bottom: 15px;
            }

            .summary-box {
              border: 1px solid #ddd;
              padding: 8px;
            }

            .summary-label {
              font-size: 9px;
              color: #666;
              margin-bottom: 3px;
            }

            .summary-value {
              font-size: 14px;
              font-weight: 700;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
            }

            th {
              background: #f1f1f1;
              font-weight: 700;
              text-align: left;
            }

            th,
            td {
              border: 1px solid #ccc;
              padding: 6px 5px;
              vertical-align: top;
              word-wrap: break-word;
            }

            th:nth-child(1) {
              width: 12%;
            }

            th:nth-child(2) {
              width: 13%;
            }

            th:nth-child(3) {
              width: 8%;
            }

            th:nth-child(4) {
              width: 5%;
            }

            th:nth-child(5),
            th:nth-child(6),
            th:nth-child(7),
            th:nth-child(8),
            th:nth-child(9) {
              width: 7%;
            }

            th:nth-child(10),
            th:nth-child(11) {
              width: 6%;
            }

            th:nth-child(12) {
              width: 9%;
            }

            small {
              color: #666;
            }

            .text-right {
              text-align: right;
            }

            .text-center {
              text-align: center;
            }

            .footer {
              margin-top: 12px;
              font-size: 10px;
              color: #666;
              text-align: right;
            }

            thead {
              display: table-header-group;
            }

            tr {
              page-break-inside: avoid;
            }
          </style>
        </head>

        <body>

          <div class="header">
            <div>
              <div class="title">
                Distribution Report
              </div>

              <div class="subtitle">
                Complete distribution transaction report
              </div>
            </div>

            <div class="date">
              Printed on<br>
              ${new Date().toLocaleString()}
            </div>
          </div>

          ${filterText.length
          ? `
                <div class="filters">
                  <strong>Filters:</strong>
                  ${filterText.join(" &nbsp; | &nbsp; ")}
                </div>
              `
          : ""
        }

          <div class="summary">

            <div class="summary-box">
              <div class="summary-label">
                Records
              </div>

              <div class="summary-value">
                ${Number(printSummary.records || 0)}
              </div>
            </div>

            <div class="summary-box">
              <div class="summary-label">
                Quantity
              </div>

              <div class="summary-value">
                ${Number(printSummary.quantity || 0)}
              </div>
            </div>

            <div class="summary-box">
              <div class="summary-label">
                Final Amount
              </div>

              <div class="summary-value">
                ${formatAmount(printSummary.final_amount)}
              </div>
            </div>

            <div class="summary-box">
              <div class="summary-label">
                Paid Amount
              </div>

              <div class="summary-value">
                ${formatAmount(printSummary.paid_amount)}
              </div>
            </div>

            <div class="summary-box">
              <div class="summary-label">
                Due Amount
              </div>

              <div class="summary-value">
                ${formatAmount(printSummary.due_amount)}
              </div>
            </div>

          </div>

          <table>

            <thead>
              <tr>
                <th>Recipient</th>
                <th>Kit / Item</th>
                <th>Batch</th>
                <th>Qty</th>
                <th>Kit Amount</th>
                <th>Discount</th>
                <th>Final Amount</th>
                <th>Paid</th>
                <th>Due</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>

            <tbody>
              ${rowsHtml}
            </tbody>

          </table>

          <div class="footer">
            Total records printed:
            <strong>${allTransactions.length}</strong>
          </div>

        </body>
      </html>
    `);

      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        }, 300);
      };
    } catch (error) {
      console.error(error);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to print report.",
      );
    } finally {
      setPrinting(false);
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="container-fluid flex-grow-1 container-p-y">
              <DistributionKitLayout />

              {/* ------------------------------------------ */}
              {/* Header */}
              {/* ------------------------------------------ */}

              <div className="card border-0 shadow-sm mb-4 overflow-hidden">
                <div className="card-body p-4">
                  <div className="row align-items-center g-4">
                    <div className="col-xl-5 col-lg-12">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-3 bg-label-primary"
                          style={{
                            width: 54,
                            height: 54,
                            minWidth: 54,
                          }}
                        >
                          <i className="ri-file-chart-line fs-3 text-primary" />
                        </div>

                        <div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <h4 className="mb-0 fw-bold">
                              Distribution Report
                            </h4>

                            <span className="badge bg-label-primary rounded-pill">
                              {stats.records} Records
                            </span>
                          </div>

                          <p className="text-muted mb-0 mt-1">
                            View kit distribution, recipients and outstanding
                            amounts
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-7 col-lg-12">
                      <div className="row g-3 justify-content-xl-end">
                        <div className="col-sm-4">
                          <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-label-primary h-100">
                            <div
                              className="d-flex align-items-center justify-content-center rounded-2 bg-primary text-white"
                              style={{
                                width: 40,
                                height: 40,
                                minWidth: 40,
                              }}
                            >
                              <i className="ri-stack-line fs-5" />
                            </div>

                            <div>
                              <div className="text-muted small">Quantity</div>

                              <div className="fs-5 fw-bold">
                                {stats.quantity}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-sm-4">
                          <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-label-success h-100">
                            <div
                              className="d-flex align-items-center justify-content-center rounded-2 bg-success text-white"
                              style={{
                                width: 40,
                                height: 40,
                                minWidth: 40,
                              }}
                            >
                              <i className="ri-money-rupee-circle-line fs-5" />
                            </div>

                            <div>
                              <div className="text-muted small">
                                Final Amount
                              </div>

                              <div className="fs-5 fw-bold text-success">
                                ₹{stats.finalAmount.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-sm-4">
                          <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-label-warning h-100">
                            <div
                              className="d-flex align-items-center justify-content-center rounded-2 bg-warning text-white"
                              style={{
                                width: 40,
                                height: 40,
                                minWidth: 40,
                              }}
                            >
                              <i className="ri-wallet-3-line fs-5" />
                            </div>

                            <div>
                              <div className="text-muted small">Due Amount</div>

                              <div className="fs-5 fw-bold text-warning">
                                ₹{stats.dueAmount.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ------------------------------------------ */}
              {/* Report */}
              {/* ------------------------------------------ */}

              <div className="card border-0 shadow-sm">
                <div className="card-header border-bottom px-3 px-md-4 py-3">
                  <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div>
                      <h5 className="mb-1 fw-bold">
                        Distribution Transactions
                      </h5>

                      <p className="text-muted small mb-0">
                        Track kits distributed to students and staff
                      </p>
                    </div>

                    <div className="d-flex align-items-center gap-2">

                      {hasAccess("/distribution/report/print") && (

                        <button
                          type="button"
                          className="btn btn-label-success"
                          onClick={printReport}
                          disabled={printing}
                        >
                          <i
                            className={`${printing ? "ri-loader-4-line" : "ri-printer-line"
                              } me-1`}
                          />

                          {printing ? "Preparing..." : "Print"}
                        </button>
                      )}
                      <div
                        className="input-group"
                        style={{
                          minWidth: 260,
                          height: 42,
                        }}
                      >
                        <span className="input-group-text bg-transparent">
                          <i className="ri-search-line" />
                        </span>

                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search recipient..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </div>

                      <button
                        type="button"
                        className={`btn ${hasFilters ? "btn-primary" : "btn-label-primary"
                          }`}
                        onClick={openFilters}
                      >
                        <i className="ri-filter-3-line me-1" />
                        Filters
                        {hasFilters && (
                          <span className="badge bg-white text-primary ms-2">
                            {Object.values(filters).filter(
                              (value) => value !== "",
                            ).length + (search ? 1 : 0)}
                          </span>
                        )}
                      </button>

                      {hasFilters && (
                        <button
                          type="button"
                          className="btn btn-label-secondary"
                          onClick={clearFilters}
                        >
                          <i className="ri-close-line me-1" />
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Table */}

                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table
                      className="table table-hover align-middle mb-0"
                      style={{ minWidth: 1400 }}
                    >
                      <thead>
                        <tr>
                          <th className="ps-4">Recipient</th>
                          <th>Kit / Item</th>
                          <th>Batch</th>
                          <th>Qty</th>
                          <th>Kit Amount</th>
                          <th>Discount</th>
                          <th>Final Amount</th>
                          <th>Paid</th>
                          <th>Due</th>
                          <th>Payment</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {transactions.length === 0 && !loading && (
                          <tr>
                            <td colSpan={13}>
                              <div className="text-center py-5">
                                <div
                                  className="d-flex align-items-center justify-content-center rounded-circle bg-label-primary mx-auto mb-3"
                                  style={{
                                    width: 64,
                                    height: 64,
                                  }}
                                >
                                  <i className="ri-file-chart-line fs-2 text-primary" />
                                </div>

                                <h6 className="mb-1 fw-semibold">
                                  No distribution records found
                                </h6>

                                <p className="text-muted small mb-0">
                                  Try changing your search or filters.
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}

                        {transactions.map((row) => {
                          const recipient = getRecipient(row);

                          return (
                            <tr key={row.id}>
                              {/* Recipient */}

                              <td className="ps-4">
                                <div className="d-flex align-items-center gap-3">
                                  <div
                                    className={`d-flex align-items-center justify-content-center rounded-circle ${recipient.type === "Staff"
                                      ? "bg-label-info"
                                      : "bg-label-primary"
                                      }`}
                                    style={{
                                      width: 40,
                                      height: 40,
                                      minWidth: 40,
                                    }}
                                  >
                                    <i
                                      className={
                                        recipient.type === "Staff"
                                          ? "ri-user-star-line text-info"
                                          : "ri-user-line text-primary"
                                      }
                                    />
                                  </div>

                                  <div>
                                    <div className="fw-semibold">
                                      {recipient.name}
                                    </div>

                                    <div className="text-muted small">
                                      {recipient.type}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* Kit / Item */}

                              <td>
                                <div className="fw-semibold">
                                  {row.kit?.name || "—"}
                                </div>

                                {row.item ? (
                                  <div className="text-muted small mt-1">
                                    <i className="ri-box-3-line me-1" />
                                    {row.item.name}
                                  </div>
                                ) : (
                                  <div className="text-muted small">
                                    Complete Kit
                                  </div>
                                )}
                              </td>

                              {/* Batch */}

                              <td>
                                {row.batch ? (
                                  <span className="badge bg-label-info">
                                    {row.batch.title}
                                  </span>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>

                              {/* Quantity */}

                              <td>
                                <span className="fw-semibold">
                                  {row.quantity}
                                </span>
                              </td>

                              {/* Kit Amount */}

                              <td>₹{Number(row.kit_amount || 0).toFixed(2)}</td>

                              {/* Discount */}

                              <td>
                                {Number(row.discount_amount || 0) > 0 ? (
                                  <span className="text-success">
                                    - ₹{Number(row.discount_amount).toFixed(2)}
                                  </span>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>

                              {/* Final */}

                              <td>
                                <span className="fw-bold">
                                  ₹{Number(row.final_amount || 0).toFixed(2)}
                                </span>
                              </td>

                              {/* Paid */}

                              <td>
                                <span className="text-success fw-semibold">
                                  ₹{Number(row.paid_amount || 0).toFixed(2)}
                                </span>
                              </td>

                              {/* Due */}

                              <td>
                                <span
                                  className={
                                    Number(row.due_amount || 0) > 0
                                      ? "text-danger fw-semibold"
                                      : "text-success fw-semibold"
                                  }
                                >
                                  ₹{Number(row.due_amount || 0).toFixed(2)}
                                </span>
                              </td>

                              {/* Payment */}

                              <td>{getPaymentBadge(row.payment_status)}</td>

                              {/* Status */}

                              <td>{getStatusBadge(row.status)}</td>

                              {/* Date */}

                              <td>
                                <div className="text-nowrap">
                                  {row.created_at
                                    ? formatDateTime(row.created_at)
                                    : "—"}
                                </div>
                              </td>
                              <td className="text-center">
                                {Number(row.due_amount || 0) > 0 ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-label-success"
                                    onClick={() =>
                                      navigate(
                                        `/distribution-kits/payment?id=${row.id}`,
                                      )
                                    }
                                    title="Pay Due"
                                  >
                                    <i className="ri-money-rupee-circle-line me-1" />
                                    Pay Due
                                  </button>
                                ) : Number(row.paid_amount || 0) > 0 ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-label-primary"
                                    onClick={() => openPaymentHistory(row)}
                                    title="Payment History"
                                  >
                                    <i className="ri-history-line me-1" />
                                    Payments
                                  </button>
                                ) : (
                                  <span className="text-muted small">
                                    <i className="ri-checkbox-circle-line me-1 text-success" />
                                    Free
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Footer */}

                {pagination && pagination.total > 0 && (
                  <div className="card-footer border-top py-3">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                      {/* Showing */}
                      <span className="text-muted small">
                        Showing{" "}
                        <strong className="text-body">
                          {pagination.from || 0}
                        </strong>{" "}
                        to{" "}
                        <strong className="text-body">
                          {pagination.to || 0}
                        </strong>{" "}
                        of{" "}
                        <strong className="text-body">
                          {pagination.total || 0}
                        </strong>{" "}
                        records
                      </span>

                      {/* Pagination */}
                      {pagination.last_page > 1 && (
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            {/* Previous */}
                            <li
                              className={`page-item ${pagination.current_page === 1 ? "disabled" : ""
                                }`}
                            >
                              <button
                                type="button"
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(pagination.current_page - 1)
                                }
                                disabled={pagination.current_page === 1}
                              >
                                <i className="ri-arrow-left-s-line" />
                              </button>
                            </li>

                            {/* Pages */}
                            {Array.from(
                              {
                                length: pagination.last_page,
                              },
                              (_, index) => index + 1,
                            )
                              .filter((page) => {
                                const current = pagination.current_page;

                                const last = pagination.last_page;

                                return (
                                  page === 1 ||
                                  page === last ||
                                  Math.abs(page - current) <= 2
                                );
                              })
                              .reduce((pages, page, index, array) => {
                                if (index > 0 && page - array[index - 1] > 1) {
                                  pages.push("...");
                                }

                                pages.push(page);

                                return pages;
                              }, [])
                              .map((page, index) => {
                                if (page === "...") {
                                  return (
                                    <li
                                      key={`ellipsis-${index}`}
                                      className="page-item disabled"
                                    >
                                      <span className="page-link">...</span>
                                    </li>
                                  );
                                }

                                return (
                                  <li
                                    key={page}
                                    className={`page-item ${pagination.current_page === page
                                      ? "active"
                                      : ""
                                      }`}
                                  >
                                    <button
                                      type="button"
                                      className="page-link"
                                      onClick={() => handlePageChange(page)}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                );
                              })}

                            {/* Next */}
                            <li
                              className={`page-item ${pagination.current_page === pagination.last_page
                                ? "disabled"
                                : ""
                                }`}
                            >
                              <button
                                type="button"
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(pagination.current_page + 1)
                                }
                                disabled={
                                  pagination.current_page ===
                                  pagination.last_page
                                }
                              >
                                <i className="ri-arrow-right-s-line" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ------------------------------------------ */}
      {/* Filter Offcanvas */}
      {/* ------------------------------------------ */}

      {showFilter && (
        <>
          <div
            className="offcanvas offcanvas-end show"
            tabIndex="-1"
            style={{
              visibility: "visible",
              width: 420,
              maxWidth: "90vw",
            }}
          >
            <div className="offcanvas-header border-bottom">
              <div>
                <h5 className="offcanvas-title fw-bold mb-1">Report Filters</h5>

                <small className="text-muted">
                  Filter distribution records
                </small>
              </div>

              <button
                type="button"
                className="btn-close"
                onClick={() => setShowFilter(false)}
              />
            </div>

            <div className="offcanvas-body">
              {/* Kit */}

              <div className="mb-3">
                <label className="form-label fw-medium">Distribution Kit</label>

                <select
                  className="form-select"
                  value={tempFilters.kit_id}
                  onChange={(e) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      kit_id: e.target.value,
                    }))
                  }
                >
                  <option value="">All Kits</option>

                  {kits.map((kit) => (
                    <option key={kit.id} value={kit.id}>
                      {kit.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Recipient Type */}

              <div className="mb-3">
                <label className="form-label fw-medium">Recipient Type</label>

                <select
                  className="form-select"
                  value={tempFilters.recipient_type}
                  onChange={(e) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      recipient_type: e.target.value,
                    }))
                  }
                >
                  <option value="">All</option>
                  <option value="student">Student</option>
                  <option value="staff">Staff</option>
                </select>
              </div>

              {/* Payment Status */}

              <div className="mb-3">
                <label className="form-label fw-medium">Payment Status</label>

                <select
                  className="form-select"
                  value={tempFilters.payment_status}
                  onChange={(e) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      payment_status: e.target.value,
                    }))
                  }
                >
                  <option value="">All</option>
                  <option value="0">Pending</option>
                  <option value="1">Partial</option>
                  <option value="2">Paid</option>
                </select>
              </div>

              {/* Assignment Status */}

              <div className="mb-3">
                <label className="form-label fw-medium">
                  Assignment Status
                </label>

                <select
                  className="form-select"
                  value={tempFilters.status}
                  onChange={(e) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                >
                  <option value="">Active + History</option>
                  <option value="1">Active</option>
                  <option value="3">Removed</option>
                  <option value="2">Deleted</option>
                </select>
              </div>
            </div>

            <div className="offcanvas-footer border-top p-3">
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-label-secondary flex-fill"
                  onClick={clearFilters}
                >
                  <i className="ri-refresh-line me-1" />
                  Clear
                </button>

                <button
                  type="button"
                  className="btn btn-primary flex-fill"
                  onClick={applyFilters}
                >
                  <i className="ri-filter-line me-1" />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>

          <div
            className="offcanvas-backdrop fade show"
            onClick={() => setShowFilter(false)}
          />
        </>
      )}
      {showPaymentHistory && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
          tabIndex="-1"
          role="dialog"
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              {/* Header */}
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">Payment History</h5>

                  {paymentHistoryFee && (
                    <small className="text-muted">
                      Fee #{paymentHistoryFee.id}
                      {" · "}
                      {paymentHistoryFee.student?.name ||
                        paymentHistoryFee.staff?.name ||
                        "-"}
                    </small>
                  )}
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPaymentHistory(false)}
                />
              </div>

              {/* Body */}
              <div className="modal-body">
                {/* Fee summary */}
                {paymentHistoryFee && (
                  <div className="row g-3 mb-4">
                    <div className="col-md-3">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Kit</div>

                        <div className="fw-semibold">
                          {paymentHistoryFee.kit?.name || "-"}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Final Amount</div>

                        <div className="fw-semibold">
                          ₹
                          {Number(paymentHistoryFee.final_amount || 0).toFixed(
                            2,
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Paid</div>

                        <div className="fw-semibold text-success">
                          ₹
                          {Number(paymentHistoryFee.paid_amount || 0).toFixed(
                            2,
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Due</div>

                        <div
                          className={`fw-semibold ${Number(paymentHistoryFee.due_amount || 0) > 0
                            ? "text-danger"
                            : "text-success"
                            }`}
                        >
                          ₹
                          {Number(paymentHistoryFee.due_amount || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Loading */}
                {paymentHistoryLoading ? (
                  <div className="py-5">
                    <LoadingDiv />
                  </div>
                ) : paymentHistory.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="ri-receipt-line fs-1 text-muted" />

                    <div className="mt-2 text-muted">
                      No payment transactions found.
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Receipt No.</th>
                          <th>Date</th>
                          <th className="text-end">Amount</th>
                          <th>Method</th>
                          <th>Account</th>
                          <th>Transaction No.</th>
                          <th className="text-center">Receipt</th>
                        </tr>
                      </thead>

                      <tbody>
                        {paymentHistory.map((transaction, index) => (
                          <tr key={transaction.id}>
                            <td>{index + 1}</td>

                            <td>
                              <span className="fw-semibold">
                                {transaction.receipt_no || `#${transaction.id}`}
                              </span>
                            </td>

                            <td>{transaction.payment_date || "-"}</td>

                            <td className="text-end">
                              <span className="fw-semibold text-success">
                                ₹{Number(transaction.amount || 0).toFixed(2)}
                              </span>
                            </td>

                            <td>
                              <span className="badge bg-label-primary">
                                {transaction.payment_method || "-"}
                              </span>
                            </td>

                            <td>{transaction.account_no || "-"}</td>

                            <td>{transaction.transaction_no || "-"}</td>

                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-label-primary"
                                onClick={() => {
                                  navigate(
                                    `/distribution-kits/payment-receipt?id=${transaction.id}`,
                                    {
                                      state: {
                                        payment: {
                                          transaction,
                                          fee: paymentHistoryFee,

                                          payment: {
                                            amount: Number(
                                              transaction.amount || 0,
                                            ),

                                            previous_paid_amount: Number(
                                              transaction.previous_paid_amount ||
                                              0,
                                            ),

                                            paid_amount: Number(
                                              transaction.paid_amount_after_payment ||
                                              0,
                                            ),

                                            due_amount: Number(
                                              transaction.due_amount_after_payment ||
                                              0,
                                            ),

                                            payment_status:
                                              Number(
                                                transaction.due_amount_after_payment ||
                                                0,
                                              ) <= 0
                                                ? 2
                                                : 1,
                                          },
                                        },
                                      },
                                    },
                                  );
                                }}
                              >
                                <i className="ri-receipt-line me-1" />
                                Receipt
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="modal-footer">
                {paymentHistoryFee &&
                  Number(paymentHistoryFee.due_amount || 0) > 0 && (
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => {
                        setShowPaymentHistory(false);

                        navigate(
                          `/distribution-kits/payment?id=${paymentHistoryFee.id}`,
                        );
                      }}
                    >
                      <i className="ri-money-rupee-circle-line me-1" />
                      Pay Due
                    </button>
                  )}

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPaymentHistory(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
